import React, { useState, useEffect } from 'react';
import Modal, { ModalTitle, ModalActions } from '../Modal/Modal';
import { PortalField } from '../FormField/PortalField';
import { PortalInput } from '../FormField/PortalInput';
import Button from '../Button/Button';
import styles from './TransferToAdopterModal.module.scss';

interface TransferToAdopterModalProps {
  open: boolean;
  onClose: () => void;
  /** Device or module identifier shown in the modal description. */
  deviceId: string;
  /** Optional pet name appended in parentheses after the device id. */
  petName?: string | null;
  /** When set, a banner notes the chip will be transferred to the adopter automatically. */
  chipSerialNumber?: string | null;
  /** Whether to show the rescue subscription discount banner. */
  showRescueDiscount?: boolean;
  /**
   * Pre-fill values, synced into the form each time the modal opens. Used when
   * re-entering the transfer after "I entered the wrong adopter info" so the
   * rescue can correct the existing details instead of retyping them.
   */
  initialFirstName?: string | null;
  initialLastName?: string | null;
  initialEmail?: string | null;
  initialPhone?: string | null;
  onTransfer: (firstName: string, lastName: string, email: string, phone: string) => void;
}

export default function TransferToAdopterModal({
  open,
  onClose,
  deviceId,
  petName,
  chipSerialNumber,
  showRescueDiscount,
  initialFirstName,
  initialLastName,
  initialEmail,
  initialPhone,
  onTransfer,
}: TransferToAdopterModalProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  // Seed the form from the pre-fill values whenever the modal opens. Keyed on
  // `open` so re-opening for a different device re-applies its details (the
  // component instance persists between opens, so state would otherwise linger).
  useEffect(() => {
    if (open) {
      setFirstName(initialFirstName ?? '');
      setLastName(initialLastName ?? '');
      setEmail(initialEmail ?? '');
      setPhone(initialPhone ?? '');
    }
  }, [open, initialFirstName, initialLastName, initialEmail, initialPhone]);

  function handleClose() {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    onClose();
  }

  const canSubmit = !!firstName.trim() && !!email.trim() && !!phone.trim();

  function handleConfirm() {
    if (!canSubmit) {
      return;
    }
    onTransfer(firstName.trim(), lastName.trim(), email.trim(), phone.trim());
    handleClose();
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalTitle>Transfer to adopter</ModalTitle>
      <p className={styles.modalDescription}>
        Transfer <strong>{deviceId}</strong>
        {petName ? ` (${petName})` : ''} to the adopter. The adopter will receive an email to set up their own Fi
        subscription.
      </p>

      <PortalField label="Adopter name *">
        <div className={styles.fieldRow}>
          <PortalInput value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" />
          <PortalInput value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" />
        </div>
      </PortalField>

      <PortalField label="Adopter email *">
        <PortalInput
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="e.g. sarah@gmail.com"
        />
      </PortalField>

      <PortalField label="Adopter phone *">
        <PortalInput
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="e.g. (555) 555-0100"
        />
      </PortalField>

      {showRescueDiscount && (
        <div className={styles.infoBanner}>
          The adopter will receive a special rescue discount on their Fi subscription.
        </div>
      )}

      {chipSerialNumber && (
        <div className={styles.infoBanner}>
          This pet&apos;s microchip (<strong>{chipSerialNumber}</strong>) will also be transferred to the adopter
          automatically.
        </div>
      )}

      <ModalActions>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleConfirm} disabled={!canSubmit}>
          Transfer to Adopter
        </Button>
      </ModalActions>
    </Modal>
  );
}
